import { SendHorizonal, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { notification } from "antd";
import clsx from "clsx";
import {
	Box,
	TextField,
	Typography,
	Button,
	Select,
	MenuItem,
	Autocomplete,
	ListItemText,
	Checkbox,
	FormControlLabel,
	Stack,
} from "@mui/material";

import IdeaShareIdea from "../../../src/assets/Idea_ShareIdea.svg";
import hospitalsServices from "../../services/hospitalsServices";
import categoryServices from "../../services/categoryServices";
import IdeaFormRegistry from "./IdeaFormRegistry/IdeaFormRegistry";
import usersServices from "../../services/usersServices";
import ideasServices from "../../services/ideasServices";

import IllustrationFormContainerBottom from "../../assets/Form/Form3.png";
import IllustrationFormContainerTop from "../../assets/Form/Form2.png";
import IllustrationLeft from "../../assets/Form/Form1.png";
import ReachLogo from "../../assets/Logos/reach_logo.png";
import SummaryModal from "./SummaryModal/SummaryModal";
import SuccessModal from "./SuccessModal/SuccessModal";
import "./IdeaForm.scss";

export default function IdeaForm() {
	const navigator = useNavigate();

	const [openHospitalMenu, setOpenHospitalMenu] = useState<boolean>(false);
	const [selectedHospitals, setSelectedHospitals] = useState<any>([]);

	const [openSummaryModal, setOpenSummaryModal] = useState<boolean>(false);
	const [openSuccessModal, setOpenSuccessModal] = useState<boolean>(false);
	const [selectedAuthors, setSelectedAuthors] = useState<any>([]);
	const [selectedCategory, setSelectedCategory] = useState<any>();
	const [selectedAreas, setSelectedAreas] = useState<any[]>([]);
	const [hospitalAreas, setHospitalAreas] = useState<any>([]);
	const [description, setDescription] = useState<string>("");
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [userOptions, setUserOptions] = useState<any>([]);
	const [resources, setResources] = useState<string>("");
	const [categories, setCategories] = useState<any>([]);
	const [hospitals, setHospitals] = useState<any[]>([]);
	const [currentStep, setCurrentStep] = useState(1);
	const [title, setTitle] = useState<string>("");

	const getAllActiveHospitals = () => {
		hospitalsServices.getAllActiveHospitals().then((activeHospitals) => {
			let aux = [];
			for (let hospital of activeHospitals) {
				aux.push({
					id: hospital["id"],
					name: hospital["hospital_name"],
				});
			}
			setHospitals(aux);
		});
	};

	const getUsersPerHospital = () => {
		usersServices.getUsersPerHospital(selectedHospitals).then((users) => {
			let aux = [];
			for (let user of users) {
				aux.push({
					name: `${user["first_name"]} ${user["last_name"]}`,
					id: user["id"],
					hospital: user["hospital"],
				});
			}
			setUserOptions(aux);
		});
	};

	const getAreas = () => {
		hospitalsServices.getAllAreas().then((hospitalAreas) => {
			let aux = [];
			for (let hospitalArea of hospitalAreas) {
				aux.push({
					name: hospitalArea["area_name"],
					id: hospitalArea["id"],
				});
			}
			setHospitalAreas(aux);
		});
	};

	const getAllCategories = () => {
		categoryServices.getAllCategories().then((allCategories) => {
			let aux = [];
			for (let category of allCategories) {
				aux.push({
					name: category["category"],
					id: category["id"],
				});
			}
			setCategories(aux);
		});
	};

	useEffect(() => {
		getAllCategories();
		getAllActiveHospitals();
		getAreas();
	}, []);

	useEffect(() => {
		if (selectedHospitals.length > 0) {
			getUsersPerHospital();
		}
	}, [selectedHospitals]);

	const createIdea = async () => {
		setIsLoading(true);
		let body = {
			authors: selectedAuthors.map((author: any) => author.id),
			title: title,
			description: description,
			category_id: selectedCategory.id,
			hospital_area_id: selectedAreas.map((area: any) => area.id),
			resources: resources,
		};
		if (checkFilledForm()) {
			await ideasServices
				.createIdea(body)
				.then(() => {
					setOpenSuccessModal(true);
					clearForm();
				})
				.catch(() => {
					notification.error({
						message: "Ups!! algo salió mal.",
					});
				})
				.finally(() => {
					setIsLoading(false);
				});
		}
	};

	const checkFilledForm = () => {
		return (
			title &&
			description &&
			selectedHospitals.length > 0 &&
			selectedAuthors.length > 0 &&
			selectedCategory &&
			selectedAreas.length > 0 &&
			resources
		);
	};

	const clearForm = () => {
		setTitle("");
		setSelectedAuthors([]);
		setSelectedAreas([]);
		setDescription("");
		setResources("");
		setSelectedCategory(null);
		setSelectedHospitals([]);
		setCurrentStep(1);
	};

	const handleNext = () => {
		currentStep === 1 ? setCurrentStep(currentStep + 1) : setOpenSummaryModal(true);
	};

	const handleBack = () => {
		currentStep === 1 ? navigator(-1) : setCurrentStep(currentStep - 1);
	};

	const addHospital = (hospital: any) => {
		setSelectedHospitals((prev: any) => {
			if (prev.some((h: any) => h.id === hospital.id)) return prev;
			return [...prev, hospital];
		});
	};

	const deleteHospital = (hospital: any) => {
		setSelectedHospitals((prev: any) =>
			prev.filter((h: any) => h.id !== hospital.id),
		);
	};

	const changeSelectedAuthors = (author: any) => {
		setSelectedAuthors((prev: any[]) => {
			const exists = prev.includes(author);
			if (exists) {
				return prev.filter((h) => h.id !== author.id);
			} else {
				return [...prev, author];
			}
		});
	};

	return (
		<>
			<Box className="form">
				<Box className="form__grain" />

				<img
					src={IllustrationLeft}
					alt=""
					style={{
						height: "100%",
						zIndex: -1,
						position: "absolute",
						bottom: 0,
					}}
				/>

				<img
					className="form__logo"
					src={ReachLogo}
					alt=""
				/>

				<Box className="form__container">
					<img
						src={IllustrationFormContainerTop}
						alt=""
						style={{
							height: "30rem",
							zIndex: -1,
							position: "absolute",
							top: 0,
							right: 0,
						}}
					/>

					<img
						src={IllustrationFormContainerBottom}
						alt=""
						style={{
							height: "20rem",
							zIndex: -1,
							position: "absolute",
							bottom: 0,
							left: 0,
						}}
					/>

					<Box className="form__container__header">
						<img
							src={IdeaShareIdea}
							alt=""
						/>

						<Box>
							<Typography className="form__container__header__title">
								¿Tienes una idea brillante?
							</Typography>
							<Typography className="form__container__header__subtitle">
								Comparte tu idea y ayúdanos a crecer juntos.
							</Typography>
						</Box>
					</Box>

					<Box className="form__container__info">
						{/* FIRST STEP */}
						{currentStep === 1 && (
							<>
								<IdeaFormRegistry
									text="Unidad(es)"
									tooltipText="Selecciona las unidades involucradas o que se verían beneficiadas."
								/>

								<Select
									multiple
									value={selectedHospitals}
									open={openHospitalMenu}
									onOpen={() => setOpenHospitalMenu(true)}
									onClose={() => setOpenHospitalMenu(false)}
									className="form__container__info__select"
									renderValue={(selected) => {
										return (
											<Box
												className="form__container__info__select__labels"
												onClick={() => setOpenHospitalMenu(true)}
											>
												{selectedHospitals.map(
													(hospital: any) => (
														<Button
															className="form__container__info__select__label"
															onClick={(e) => {
																e.stopPropagation();
																deleteHospital(hospital);
															}}
															onMouseDown={(event) => {
																event.stopPropagation();
															}}
														>
															<Typography>
																{hospital.name}
															</Typography>

															<X />
														</Button>
													),
												)}
											</Box>
										);
									}}
									MenuProps={{
										PaperProps: {
											className:
												"form__container__info__select__menu",
										},
									}}
								>
									{hospitals
										.sort((a: any, b: any) =>
											a.name.localeCompare(b.name),
										)
										.sort((a: any, b: any) =>
											selectedHospitals.includes(a)
												? -1
												: selectedHospitals.includes(b)
													? 1
													: 0,
										)
										.map((opt: any) => {
											const isSelected = selectedHospitals.some(
												(h: any) => h.id === opt.id,
											);

											return (
												<MenuItem
													key={opt.id}
													value={opt}
													disableRipple
													onClick={() => {
														if (isSelected) {
															deleteHospital(opt);
														} else {
															addHospital(opt);
														}
													}}
												>
													<Checkbox checked={isSelected} />
													<ListItemText primary={opt.name} />
												</MenuItem>
											);
										})}
								</Select>

								{/* AUTORES */}
								<IdeaFormRegistry
									text="Autor(es)"
									tooltipText=" Escribe los nombres de las personas que colaboraron en esta idea."
								/>
								<Autocomplete
									multiple
									disableCloseOnSelect
									options={userOptions}
									onChange={(event, newValue) =>
										setSelectedAuthors(newValue)
									}
									value={selectedAuthors}
									size="small"
									filterSelectedOptions
									getOptionLabel={(option) => option.name}
									isOptionEqualToValue={(option, value) =>
										option.id === value.id
									}
									renderInput={(params) => (
										<TextField
											{...params}
											className={clsx(
												"form__container__info__autocomplete",
												"form__container__info__autocomplete--expanded",
											)}
										/>
									)}
									renderValue={(selected) => {
										return (
											<>
												{selected.map((author: any) => (
													<Button
														className="form__container__info__autocomplete__label"
														onClick={() =>
															changeSelectedAuthors(author)
														}
													>
														<Typography>
															{author.name}
														</Typography>
														<X />
													</Button>
												))}
											</>
										);
									}}
									disablePortal
									slotProps={{
										paper: {
											className:
												"form__container__info__autocomplete__menu",
										},
										popper: {
											modifiers: [
												{
													name: "offset",
													options: {
														offset: [0, 55],
													},
												},
											],
										},
									}}
								/>
							</>
						)}

						{/* SECOND STEP */}
						{currentStep === 2 && (
							<>
								{/* NOMBRE */}
								<IdeaFormRegistry
									text="Nombre de idea"
									tooltipText="Dale un título breve y claro a tu idea."
								/>
								<TextField
									className="form__container__info__textfield"
									value={title}
									onChange={(e) => setTitle(e.currentTarget.value)}
									size="small"
								/>

								{/* DESCRIPCIÓN */}
								<IdeaFormRegistry
									text="Descripción"
									tooltipText="Explica tu idea en pocas líneas. ¿Qué problema resuelve y cómo?"
								/>
								<TextField
									multiline
									className={clsx(
										"form__container__info__textfield",
										"form__container__info__textfield--big",
									)}
									value={description}
									onChange={(e) =>
										setDescription(e.currentTarget.value)
									}
								/>

								<Stack
									flexDirection={"row"}
									gap={"20px"}
								>
									<Box flex={"1"}>
										{/* CATEGORÍA */}
										<IdeaFormRegistry
											text="Categoría"
											tooltipText="Elige la categoría que mejor describa tu idea."
										/>
										<Select
											className="form__container__info__select"
											value={selectedCategory}
											onChange={(e) => {
												setSelectedCategory(e.target.value);
											}}
											size="small"
											MenuProps={{
												PaperProps: {
													className:
														"form__container__info__select__menu",
												},
											}}
										>
											{categories.map((category: any) => (
												<MenuItem
													key={category.id}
													value={category}
												>
													{category.name}
												</MenuItem>
											))}
										</Select>
									</Box>

									{/* AREA */}
									<Box flex={"2"}>
										<IdeaFormRegistry
											text="Área(s)"
											tooltipText="Selecciona las áreas involucradas o que se verían beneficiadas."
										/>
										<Autocomplete
											multiple
											options={hospitalAreas
												.sort((a: any, b: any) =>
													a.name.localeCompare(b.name),
												)
												.sort((a: any, b: any) =>
													selectedAreas.includes(a)
														? -1
														: selectedAreas.includes(b)
															? 1
															: 0,
												)}
											value={selectedAreas}
											disableCloseOnSelect
											onChange={(event, newValue) =>
												setSelectedAreas(newValue)
											}
											getOptionLabel={(option) => option.name}
											isOptionEqualToValue={(option, value) =>
												option.id === value.id
											}
											renderOption={(
												props,
												option,
												{ selected },
											) => (
												<li {...props}>
													<FormControlLabel
														control={
															<Checkbox
																className="form__container__info__select__checkbox"
																checked={selected}
															/>
														}
														label={
															<ListItemText>
																<Typography>
																	{option.name}
																</Typography>
															</ListItemText>
														}
													/>
												</li>
											)}
											renderValue={(selected) => {
												return (
													<>
														{selected.length === 1 ? (
															selected.map((area: any) => (
																<Typography className="form__container__info__autocomplete__label">
																	{area.name}
																</Typography>
															))
														) : selected.length > 1 ? (
															<Typography className="form__container__info__autocomplete__label">
																{selected.length} áreas
																seleccionadas
															</Typography>
														) : (
															() => {}
														)}
													</>
												);
											}}
											renderInput={(params) => (
												<TextField
													{...params}
													className={clsx(
														"form__container__info__autocomplete",
														"form__container__info__autocomplete--centered",
													)}
												/>
											)}
											slotProps={{
												paper: {
													className:
														"form__container__info__autocomplete__menu",
												},
												popper: {
													modifiers: [
														{
															name: "flip",
															enabled: false,
														},
														{
															name: "preventOverflow",
															options: {
																altAxis: false,
															},
														},
														{
															name: "offset",
															options: {
																offset: [0, 8],
															},
														},
													],
												},
											}}
										/>
									</Box>
								</Stack>

								{/* RECURSOS */}
								<IdeaFormRegistry
									text="Recursos necesarios"
									tooltipText="¿Tu idea requiere algo específico para implementarse? Cuéntanos aquí."
								/>
								<TextField
									multiline
									value={resources}
									onChange={(e) => setResources(e.currentTarget.value)}
									className={clsx(
										"form__container__info__textfield",
										"form__container__info__textfield--big",
									)}
								/>
							</>
						)}
					</Box>

					<Stack className="form__container__footer">
						<Button
							className={clsx(
								"form__container__footer__button",
								"form__container__footer__button--return",
							)}
							onClick={handleBack}
						>
							<Typography>Volver atrás</Typography>
						</Button>

						<Button
							className={clsx(
								"form__container__footer__button",
								"form__container__footer__button--next",
							)}
							onClick={handleNext}
							disabled={
								(currentStep === 1 && selectedAuthors.length <= 0) ||
								(currentStep !== 1 &&
									(title === "" ||
										description === "" ||
										selectedCategory === undefined ||
										selectedAreas.length <= 0 ||
										resources === ""))
							}
						>
							{currentStep === 1 ? (
								<Typography>Siguiente</Typography>
							) : (
								<>
									<Typography>Enviar idea</Typography>
									<SendHorizonal />
								</>
							)}
						</Button>
					</Stack>
				</Box>

				<SummaryModal
					open={openSummaryModal}
					onClose={() => setOpenSummaryModal(false)}
					onSave={() => createIdea()}
					selectedHospitals={selectedHospitals}
					selectedAuthors={selectedAuthors}
					title={title}
					description={description}
					selectedCategory={selectedCategory}
					selectedAreas={selectedAreas}
					resources={resources}
				/>

				<SuccessModal
					open={openSuccessModal}
					onClose={() => setOpenSuccessModal(false)}
				/>
			</Box>

			{isLoading && (
				<Box className="loader-overlay">
					<Box className="loader-container" />
					<Box className="custom-loader" />
				</Box>
			)}
		</>
	);
}
